package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerInputScope
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.CustomAccessibilityAction
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.customActions
import androidx.compose.ui.semantics.onClick
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import org.example.project.client.model.BlockType
import org.example.project.planner.BlockDraft
import org.example.project.planner.minutesToTime
import org.example.project.planner.timeToMinutes
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors
import kotlin.math.max

internal const val DP_PER_MINUTE = 2.0f
internal const val BLOCK_VERTICAL_INSET_DP = 4.0f
internal const val MIN_CARD_HEIGHT_DP = 6.0f

fun calculateBlockGeometry(startTime: String, endTime: String, eventStartTime: String): Pair<Float, Float> {
    val eventStartMin = timeToMinutes(eventStartTime.ifBlank { "10:00" })
    val bStart = timeToMinutes(startTime)
    val bEnd = timeToMinutes(endTime)
    val topDp = ((bStart - eventStartMin) * DP_PER_MINUTE).coerceAtLeast(0f)
    val durationMin = (bEnd - bStart).coerceAtLeast(5)
    val heightDp = durationMin * DP_PER_MINUTE
    return Pair(topDp, heightDp)
}

fun calculateCardGeometry(blockTopDp: Float, blockHeightDp: Float): Pair<Float, Float> {
    val inset = if (blockHeightDp < 24.0f) {
        (blockHeightDp * 0.1f).coerceIn(1.0f, BLOCK_VERTICAL_INSET_DP)
    } else {
        BLOCK_VERTICAL_INSET_DP
    }
    val cardTopDp = blockTopDp + inset
    val cardHeightDp = (blockHeightDp - (inset * 2)).coerceAtLeast(MIN_CARD_HEIGHT_DP)
    return Pair(cardTopDp, cardHeightDp)
}

data class GripBounds(
    val leftDp: Float,
    val topDp: Float,
    val widthDp: Float,
    val heightDp: Float
) {
    val rightDp: Float get() = leftDp + widthDp
    val bottomDp: Float get() = topDp + heightDp

    fun intersects(other: GripBounds): Boolean {
        return !(rightDp <= other.leftDp || other.rightDp <= leftDp ||
                 bottomDp <= other.topDp || other.bottomDp <= topDp)
    }

    fun contains(x: Float, y: Float): Boolean {
        return x in leftDp..rightDp && y in topDp..bottomDp
    }
}

data class BlockGripsGeometry(
    val topGrip: GripBounds,
    val bottomGrip: GripBounds,
    val isStaggeredHorizontally: Boolean,
    val touchHeightDp: Float,
    val verticalTouchOverflowDp: Float
)

fun calculateGripGeometry(
    blockWidthDp: Float,
    cardHeightDp: Float,
    minTouchHeightDp: Float = 24.0f
): BlockGripsGeometry {
    val safeWidth = blockWidthDp.coerceAtLeast(2.0f)
    val safeHeight = cardHeightDp.coerceAtLeast(2.0f)
    val isShort = safeHeight < 36.0f

    return if (isShort) {
        val touchHeight = maxOf(safeHeight, minTouchHeightDp)
        val verticalTouchOverflow = (touchHeight - safeHeight) / 2f
        val minCorridor = 8.0f
        val centralTapCorridor = if (safeWidth >= 20.0f) {
            maxOf(minCorridor, safeWidth * 0.2f).coerceAtMost(safeWidth - 4.0f)
        } else {
            (safeWidth * 0.3f).coerceAtLeast(1.0f)
        }
        val maxGripWidth = ((safeWidth - centralTapCorridor) / 2f).coerceAtLeast(1.0f)
        val gripWidth = minOf(44.0f, maxGripWidth)
        val rightLeft = safeWidth - gripWidth

        BlockGripsGeometry(
            topGrip = GripBounds(
                leftDp = 0f,
                topDp = 0f,
                widthDp = gripWidth,
                heightDp = touchHeight
            ),
            bottomGrip = GripBounds(
                leftDp = rightLeft,
                topDp = 0f,
                widthDp = gripWidth,
                heightDp = touchHeight
            ),
            isStaggeredHorizontally = true,
            touchHeightDp = touchHeight,
            verticalTouchOverflowDp = verticalTouchOverflow
        )
    } else {
        val touchHeight = safeHeight
        val gripWidth = minOf(48.0f, safeWidth)
        val left = ((safeWidth - gripWidth) / 2f).coerceAtLeast(0f)
        val gripHeight = minOf(24.0f, (safeHeight / 2f - 1.0f).coerceAtLeast(1.0f))

        BlockGripsGeometry(
            topGrip = GripBounds(
                leftDp = left,
                topDp = 0f,
                widthDp = gripWidth,
                heightDp = gripHeight
            ),
            bottomGrip = GripBounds(
                leftDp = left,
                topDp = safeHeight - gripHeight,
                widthDp = gripWidth,
                heightDp = gripHeight
            ),
            isStaggeredHorizontally = false,
            touchHeightDp = touchHeight,
            verticalTouchOverflowDp = 0f
        )
    }
}

fun resolveBlockClickAction(
    isTimelineEditMode: Boolean,
    onSelect: () -> Unit,
    onEdit: () -> Unit
): () -> Unit = {
    if (isTimelineEditMode) {
        onSelect()
    } else {
        onEdit()
    }
}

data class GripAccessibilityAction(
    val label: String,
    val deltaMinutes: Int,
    val execute: () -> Unit
)

fun createTopGripAccessibilityActions(
    onResizeStart: (Int) -> Unit
): List<GripAccessibilityAction> = listOf(
    GripAccessibilityAction(
        label = "Rozpocznij 5 minut wcześniej",
        deltaMinutes = -5,
        execute = { onResizeStart(-5) }
    ),
    GripAccessibilityAction(
        label = "Rozpocznij 5 minut później",
        deltaMinutes = 5,
        execute = { onResizeStart(5) }
    )
)

fun createBottomGripAccessibilityActions(
    onResizeEnd: (Int) -> Unit
): List<GripAccessibilityAction> = listOf(
    GripAccessibilityAction(
        label = "Zakończ 5 minut później",
        deltaMinutes = 5,
        execute = { onResizeEnd(5) }
    ),
    GripAccessibilityAction(
        label = "Zakończ 5 minut wcześniej",
        deltaMinutes = -5,
        execute = { onResizeEnd(-5) }
    )
)

class TimelineBlockGestureController(
    val stepPx: Float,
    val onSelect: () -> Unit,
    val onShiftBlock: (minutes: Int) -> Unit
) {
    var isDragging = false
        private set
    var totalDragY = 0f
        private set
    var accumulatedShiftY = 0f
        private set

    fun onDown(y: Float = 0f) {
        isDragging = false
        totalDragY = 0f
        accumulatedShiftY = 0f
    }

    fun onMove(deltaY: Float, touchSlop: Float) {
        totalDragY += deltaY
        if (!isDragging) {
            if (kotlin.math.abs(totalDragY) > touchSlop) {
                isDragging = true
                accumulatedShiftY = totalDragY
                checkAndDispatchShift()
            }
        } else {
            accumulatedShiftY += deltaY
            checkAndDispatchShift()
        }
    }

    private fun checkAndDispatchShift() {
        val steps = (accumulatedShiftY / stepPx).toInt()
        if (steps != 0) {
            accumulatedShiftY -= steps * stepPx
            onShiftBlock(steps * 5)
        }
    }

    fun onUp(): Boolean {
        return if (!isDragging) {
            onSelect()
            true
        } else {
            isDragging = false
            accumulatedShiftY = 0f
            totalDragY = 0f
            false
        }
    }

    fun onCancel() {
        isDragging = false
        accumulatedShiftY = 0f
        totalDragY = 0f
    }
}

suspend fun PointerInputScope.detectTapOrVerticalDragGestures(
    controller: TimelineBlockGestureController
) {
    awaitEachGesture {
        val down = awaitFirstDown(requireUnconsumed = false)
        val pointerId = down.id
        controller.onDown(down.position.y)
        val touchSlop = viewConfiguration.touchSlop

        while (true) {
            val event = awaitPointerEvent()
            val change = event.changes.firstOrNull { it.id == pointerId } ?: break

            if (change.isConsumed) {
                controller.onCancel()
                break
            }

            if (!change.pressed) {
                change.consume()
                controller.onUp()
                break
            }

            val dragY = change.position.y - change.previousPosition.y
            if (dragY != 0f) {
                controller.onMove(dragY, touchSlop)
                if (controller.isDragging) {
                    change.consume()
                }
            }
        }
    }
}

fun Modifier.timelineBlockBodyInteraction(
    isTimelineEditMode: Boolean,
    controller: TimelineBlockGestureController,
    onEdit: () -> Unit
): Modifier = if (isTimelineEditMode) {
    this.pointerInput(controller) {
        detectTapOrVerticalDragGestures(controller)
    }
} else {
    this.clickable { onEdit() }
}

fun calculateBlockLanes(blocks: List<BlockDraft>): Map<String, Pair<Int, Int>> {
    val sorted = blocks.sortedWith(compareBy({ timeToMinutes(it.startTime) }, { timeToMinutes(it.endTime) }))
    val result = mutableMapOf<String, Pair<Int, Int>>()
    val clusters = mutableListOf<MutableList<BlockDraft>>()

    for (block in sorted) {
        val bStart = timeToMinutes(block.startTime)
        val bEnd = timeToMinutes(block.endTime)
        val matchingCluster = clusters.firstOrNull { cluster ->
            cluster.any { other ->
                val oStart = timeToMinutes(other.startTime)
                val oEnd = timeToMinutes(other.endTime)
                bStart < oEnd && oStart < bEnd
            }
        }
        if (matchingCluster != null) {
            matchingCluster.add(block)
        } else {
            clusters.add(mutableListOf(block))
        }
    }

    for (cluster in clusters) {
        val laneEnds = mutableListOf<Int>()
        val clusterAssignments = mutableMapOf<String, Int>()
        for (block in cluster) {
            val bStart = timeToMinutes(block.startTime)
            val bEnd = timeToMinutes(block.endTime)
            var placed = false
            for (laneIdx in laneEnds.indices) {
                if (laneEnds[laneIdx] <= bStart) {
                    clusterAssignments[block.id] = laneIdx
                    laneEnds[laneIdx] = bEnd
                    placed = true
                    break
                }
            }
            if (!placed) {
                clusterAssignments[block.id] = laneEnds.size
                laneEnds.add(bEnd)
            }
        }
        val totalLanesInCluster = max(1, laneEnds.size)
        for (block in cluster) {
            result[block.id] = Pair(clusterAssignments[block.id] ?: 0, totalLanesInCluster)
        }
    }
    return result
}

fun calculateFreeGaps(
    startMin: Int,
    endMin: Int,
    blocks: List<BlockDraft>
): List<Pair<Int, Int>> {
    if (blocks.isEmpty()) {
        return listOf(Pair(startMin, endMin))
    }
    val sortedIntervals = blocks
        .map { timeToMinutes(it.startTime) to timeToMinutes(it.endTime) }
        .sortedBy { it.first }

    val merged = mutableListOf<Pair<Int, Int>>()
    for (interval in sortedIntervals) {
        if (merged.isEmpty()) {
            merged.add(interval)
        } else {
            val last = merged.last()
            if (interval.first <= last.second) {
                merged[merged.lastIndex] = Pair(last.first, maxOf(last.second, interval.second))
            } else {
                merged.add(interval)
            }
        }
    }

    val gaps = mutableListOf<Pair<Int, Int>>()
    var current = startMin
    for (busy in merged) {
        val busyStart = busy.first.coerceIn(startMin, endMin)
        val busyEnd = busy.second.coerceIn(startMin, endMin)
        if (busyStart > current && (busyStart - current) >= 5) {
            gaps.add(Pair(current, busyStart))
        }
        current = maxOf(current, busyEnd)
    }
    if (endMin > current && (endMin - current) >= 5) {
        gaps.add(Pair(current, endMin))
    }
    return gaps
}

fun splitGapIntoHourlySlots(
    gapStartMin: Int,
    gapEndMin: Int
): List<Pair<Int, Int>> {
    if (gapEndMin - gapStartMin < 5) return emptyList()

    val slots = mutableListOf<Pair<Int, Int>>()
    val firstHour = (gapStartMin / 60) + 1
    val lastHour = (gapEndMin - 1) / 60

    var currentStart = gapStartMin
    for (h in firstHour..lastHour) {
        val boundary = h * 60
        if (boundary - currentStart >= 5 && gapEndMin - boundary >= 5) {
            slots.add(Pair(currentStart, boundary))
            currentStart = boundary
        }
    }
    if (gapEndMin - currentStart >= 5) {
        slots.add(Pair(currentStart, gapEndMin))
    }
    return slots
}

fun calculateFreeSlots(
    startMin: Int,
    endMin: Int,
    blocks: List<BlockDraft>
): List<Pair<Int, Int>> {
    val gaps = calculateFreeGaps(startMin, endMin, blocks)
    return gaps.flatMap { splitGapIntoHourlySlots(it.first, it.second) }
}

@Composable
fun VerticalTimelineView(
    eventStartTime: String,
    eventEndTime: String,
    blocks: List<BlockDraft>,
    isTimelineEditMode: Boolean = false,
    selectedBlockId: String? = null,
    onSelectBlock: (String?) -> Unit = {},
    onEditBlock: (BlockDraft) -> Unit,
    onDeleteBlock: (String) -> Unit,
    onShiftBlock: (blockId: String, deltaMinutes: Int) -> Unit,
    onResizeBlockStart: (blockId: String, deltaMinutes: Int) -> Unit,
    onResizeBlockEnd: (blockId: String, deltaMinutes: Int) -> Unit,
    onAddBlockAt: (startTime: String, endTime: String) -> Unit,
    onOpenAddBlockTypePicker: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    val sortedBlocks = blocks.sortedBy { timeToMinutes(it.startTime) }
    val startMin = timeToMinutes(eventStartTime.ifBlank { "10:00" })
    val endMin = timeToMinutes(eventEndTime.ifBlank { "16:00" }).coerceAtLeast(startMin + 5)
    val totalMinutes = endMin - startMin
    val totalGridHeightDp = (totalMinutes * DP_PER_MINUTE).dp

    val startHour = startMin / 60
    val endHour = (endMin + 59) / 60
    val hourMarks = (startHour..endHour).filter { hour ->
        val hMin = hour * 60
        hMin in startMin..endMin
    }

    val freeSlots = remember(startMin, endMin, sortedBlocks) {
        calculateFreeSlots(startMin, endMin, sortedBlocks)
    }

    val gutterWidth = 48.dp
    val topLabelSpace = 20.dp
    val bottomLabelSpace = 16.dp
    val totalTimelineHeight = totalGridHeightDp + topLabelSpace + bottomLabelSpace

    Column(modifier = modifier.fillMaxWidth()) {
        // --- TRUE PROPORTIONAL TIME GRID ---
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .height(totalTimelineHeight)
        ) {
            val totalWidth = maxWidth
            val blocksWidth = (totalWidth - gutterWidth).coerceAtLeast(0.dp)

            // 1. Continuous horizontal hour lines
            hourMarks.forEach { hour ->
                val hourMin = hour * 60
                val lineY = topLabelSpace + ((hourMin - startMin) * DP_PER_MINUTE).dp
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .offset(y = lineY)
                        .height(1.dp)
                        .background(PlayfulColors.BorderSubtle)
                )
            }

            // 2. Hour Labels in stable left column
            hourMarks.forEach { hour ->
                val hourMin = hour * 60
                val lineY = topLabelSpace + ((hourMin - startMin) * DP_PER_MINUTE).dp
                Text(
                    text = "${hour.toString().padStart(2, '0')}:00",
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = PlayfulColors.TextSecondary,
                    modifier = Modifier.offset(x = 4.dp, y = lineY - 17.dp)
                )
            }

            // 3. Right Area: Free Gap Slots and Positioned Proportional Blocks
            Box(
                modifier = Modifier
                    .offset(x = gutterWidth, y = topLabelSpace)
                    .size(width = blocksWidth, height = totalGridHeightDp)
            ) {
                // Free Gap Action Slots (Shadowblocks)
                freeSlots.forEach { slot ->
                    val slotStartMin = slot.first
                    val slotEndMin = slot.second
                    val slotTopDp = (slotStartMin - startMin) * DP_PER_MINUTE
                    val slotHeightDp = (slotEndMin - slotStartMin) * DP_PER_MINUTE
                    val (cardTopDp, cardHeightDp) = calculateCardGeometry(slotTopDp, slotHeightDp)

                    TimelineGapSlot(
                        fromTime = minutesToTime(slotStartMin),
                        toTime = minutesToTime(slotEndMin),
                        onAdd = { onAddBlockAt(minutesToTime(slotStartMin), minutesToTime(slotEndMin)) },
                        modifier = Modifier
                            .fillMaxWidth()
                            .offset(y = cardTopDp.dp)
                            .height(cardHeightDp.dp)
                    )
                }

                // Proportional Blocks Positioned on the Time Grid
                val laneAssignments = calculateBlockLanes(sortedBlocks)
                sortedBlocks.forEach { block ->
                    val (topDpVal, heightDpVal) = calculateBlockGeometry(block.startTime, block.endTime, eventStartTime)
                    val (cardTopDp, cardHeightDp) = calculateCardGeometry(topDpVal, heightDpVal)

                    val (laneIdx, totalLanes) = laneAssignments[block.id] ?: Pair(0, 1)
                    val blockWidth = blocksWidth / totalLanes
                    val blockX = blockWidth * laneIdx
                    val hasOverlap = totalLanes > 1
                    val isSelected = isTimelineEditMode && (selectedBlockId == block.id)

                    val blockWidthVal = blockWidth.value
                    val gripsGeom = remember(blockWidthVal, cardHeightDp) {
                        calculateGripGeometry(blockWidthVal, cardHeightDp)
                    }

                    val containerTopDp = if (isSelected) cardTopDp - gripsGeom.verticalTouchOverflowDp else cardTopDp
                    val containerHeightDp = if (isSelected) gripsGeom.touchHeightDp else cardHeightDp

                    Box(
                        modifier = Modifier
                            .width(blockWidth)
                            .offset(x = blockX, y = containerTopDp.dp)
                            .padding(horizontal = 2.dp)
                            .zIndex(if (isSelected) 10f else 1f)
                    ) {
                        TimelineBlockItem(
                            block = block,
                            heightDp = cardHeightDp.dp,
                            containerHeightDp = containerHeightDp.dp,
                            verticalTouchOverflowDp = (if (isSelected) gripsGeom.verticalTouchOverflowDp else 0f).dp,
                            gripsGeom = gripsGeom,
                            hasOverlap = hasOverlap,
                            isTimelineEditMode = isTimelineEditMode,
                            isSelected = isSelected,
                            onSelect = {
                                if (isTimelineEditMode) {
                                    onSelectBlock(if (selectedBlockId == block.id) null else block.id)
                                }
                            },
                            onEdit = { onEditBlock(block) },
                            onDelete = { onDeleteBlock(block.id) },
                            onShiftBlock = { delta -> onShiftBlock(block.id, delta) },
                            onResizeStart = { delta -> onResizeBlockStart(block.id, delta) },
                            onResizeEnd = { delta -> onResizeBlockEnd(block.id, delta) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun TimelineGapSlot(
    fromTime: String,
    toTime: String,
    onAdd: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current

    BoxWithConstraints(
        modifier = modifier.padding(horizontal = 2.dp)
    ) {
        val availableHeight = maxHeight
        val isTiny = availableHeight < 24.dp

        Surface(
            onClick = onAdd,
            shape = if (isTiny) RoundedCornerShape(4.dp) else shapes.cardSmall,
            color = PlayfulColors.CoralLight.copy(alpha = 0.35f),
            border = BorderStroke(1.dp, PlayfulColors.CoralBorder.copy(alpha = 0.6f)),
            modifier = Modifier
                .fillMaxSize()
                .semantics {
                    contentDescription = "Dodaj punkt programu, od $fromTime do $toTime"
                    role = Role.Button
                }
        ) {
            Box(modifier = Modifier.fillMaxSize())
        }
    }
}

@Composable
fun TimelineBlockItem(
    block: BlockDraft,
    heightDp: Dp,
    containerHeightDp: Dp = heightDp,
    verticalTouchOverflowDp: Dp = 0.dp,
    gripsGeom: BlockGripsGeometry? = null,
    hasOverlap: Boolean,
    isTimelineEditMode: Boolean,
    isSelected: Boolean,
    onSelect: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onShiftBlock: (Int) -> Unit,
    onResizeStart: (Int) -> Unit,
    onResizeEnd: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    val density = LocalDensity.current
    val step5MinPx = with(density) { (5f * DP_PER_MINUTE).dp.toPx() }

    var dragStartY by remember(block.id) { mutableStateOf(0f) }
    var dragEndY by remember(block.id) { mutableStateOf(0f) }

    val bodyGestureController = remember(block.id, step5MinPx, onSelect, onShiftBlock) {
        TimelineBlockGestureController(
            stepPx = step5MinPx,
            onSelect = onSelect,
            onShiftBlock = onShiftBlock
        )
    }

    val blockClickAction = remember(isTimelineEditMode, onSelect, onEdit) {
        resolveBlockClickAction(isTimelineEditMode, onSelect, onEdit)
    }
    val topActions = remember(block.id, onResizeStart) {
        createTopGripAccessibilityActions(onResizeStart)
    }
    val bottomActions = remember(block.id, onResizeEnd) {
        createBottomGripAccessibilityActions(onResizeEnd)
    }

    var isDraggingTop by remember(block.id) { mutableStateOf(false) }
    var isDraggingBottom by remember(block.id) { mutableStateOf(false) }

    val isTinyMarker = heightDp < 24.dp
    val isCompact = heightDp in 24.dp..56.dp

    val (badgeBg, badgeFg, badgeBorder) = when (block.blockType) {
        BlockType.CATALOG_GAME -> Triple(PlayfulColors.TealLight, PlayfulColors.TealDark, PlayfulColors.TealBorder)
        BlockType.CUSTOM_GAME -> Triple(PlayfulColors.LilacLight, PlayfulColors.LilacDark, PlayfulColors.LilacBorder)
        BlockType.OTHER -> Triple(PlayfulColors.SunYellowLight, PlayfulColors.SunYellowDark, PlayfulColors.SunYellowBorder)
    }

    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .height(containerHeightDp)
    ) {
        val blockWidthDp = maxWidth.value
        val cardHeightDp = heightDp.value
        val activeGripsGeom = gripsGeom ?: remember(blockWidthDp, cardHeightDp) {
            calculateGripGeometry(blockWidthDp, cardHeightDp)
        }
        val cardOffset = if (isSelected) verticalTouchOverflowDp else 0.dp

        // --- 1. MAIN CARD OR TINY MARKER ---
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .offset(y = cardOffset)
                .height(heightDp)
        ) {
            if (isTinyMarker) {
            // TINY MARKER (for short blocks < 24 dp, e.g. 5-10 min)
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(RoundedCornerShape(3.dp))
                    .background(badgeFg)
                    .timelineBlockBodyInteraction(
                        isTimelineEditMode = isTimelineEditMode,
                        controller = bodyGestureController,
                        onEdit = onEdit
                    )
                    .semantics {
                        contentDescription = "Punkt programu: ${block.title}, od ${block.startTime} do ${block.endTime}"
                        role = Role.Button
                        onClick(
                            label = if (isTimelineEditMode) {
                                if (isSelected) "Odznacz punkt programu" else "Wybierz punkt programu"
                            } else {
                                "Edytuj punkt programu"
                            }
                        ) {
                            blockClickAction()
                            true
                        }
                    }
            )

            // When tiny block is selected in edit mode, show clear floating info overlay
            if (isTimelineEditMode && isSelected) {
                Surface(
                    color = PlayfulColors.Surface,
                    shape = shapes.cardSmall,
                    border = BorderStroke(1.5.dp, PlayfulColors.Coral),
                    shadowElevation = 6.dp,
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .offset(y = (-42).dp)
                        .zIndex(20f)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Column(modifier = Modifier.padding(end = 4.dp)) {
                            Text(
                                text = block.title,
                                fontWeight = FontWeight.Bold,
                                fontSize = 11.sp,
                                color = PlayfulColors.TextPrimary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = "${block.startTime} – ${block.endTime}",
                                fontSize = 10.sp,
                                color = PlayfulColors.TextSecondary
                            )
                        }

                        IconButton(
                            onClick = onEdit,
                            modifier = Modifier.size(20.dp)
                        ) {
                            Icon(
                                imageVector = AppIcons.Edit,
                                contentDescription = "Edytuj blok",
                                tint = PlayfulColors.Coral,
                                modifier = Modifier.size(12.dp)
                            )
                        }

                        IconButton(
                            onClick = onDelete,
                            modifier = Modifier.size(20.dp)
                        ) {
                            Icon(
                                imageVector = AppIcons.Delete,
                                contentDescription = "Usuń blok",
                                tint = PlayfulColors.Error,
                                modifier = Modifier.size(12.dp)
                            )
                        }
                    }
                }
            }
        } else {
            // STANDARD / COMPACT CARD (heightDp >= 24 dp)
            Card(
                modifier = Modifier
                    .fillMaxSize()
                    .timelineBlockBodyInteraction(
                        isTimelineEditMode = isTimelineEditMode,
                        controller = bodyGestureController,
                        onEdit = onEdit
                    )
                    .semantics {
                        contentDescription = "Punkt programu: ${block.title}, od ${block.startTime} do ${block.endTime}"
                        role = Role.Button
                        onClick(
                            label = if (isTimelineEditMode) {
                                if (isSelected) "Odznacz punkt programu" else "Wybierz punkt programu"
                            } else {
                                "Edytuj punkt programu"
                            }
                        ) {
                            blockClickAction()
                            true
                        }
                    },
                shape = shapes.cardSmall,
                colors = CardDefaults.cardColors(containerColor = PlayfulColors.Surface),
                border = BorderStroke(
                    if (isSelected) 2.dp else 1.dp,
                    if (isSelected) PlayfulColors.Coral else if (hasOverlap) PlayfulColors.WarningBorder else PlayfulColors.BorderSubtle
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = if (isSelected) 4.dp else 2.dp)
            ) {
                if (isCompact) {
                    // COMPACT ROW (e.g. 15-25 min = 24-50 dp)
                    Row(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(horizontal = 6.dp, vertical = 2.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            modifier = Modifier.weight(1f),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .width(3.dp)
                                    .height(14.dp)
                                    .background(badgeFg, RoundedCornerShape(1.5.dp))
                            )
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                text = block.title,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = PlayfulColors.TextPrimary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            if (hasOverlap) {
                                Spacer(modifier = Modifier.width(4.dp))
                                Icon(
                                    imageVector = AppIcons.Warning,
                                    contentDescription = "Równoległy punkt programu",
                                    tint = PlayfulColors.OnWarningContainer,
                                    modifier = Modifier.size(11.dp)
                                )
                            }
                        }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(2.dp)
                        ) {
                            IconButton(
                                onClick = onEdit,
                                modifier = Modifier.size(22.dp)
                            ) {
                                Icon(
                                    imageVector = AppIcons.Edit,
                                    contentDescription = "Edytuj blok",
                                    tint = PlayfulColors.Coral,
                                    modifier = Modifier.size(12.dp)
                                )
                            }
                            IconButton(
                                onClick = onDelete,
                                modifier = Modifier.size(22.dp)
                            ) {
                                Icon(
                                    imageVector = AppIcons.Delete,
                                    contentDescription = "Usuń blok",
                                    tint = PlayfulColors.Error,
                                    modifier = Modifier.size(12.dp)
                                )
                            }
                        }
                    }
                } else {
                    // FULL LAYOUT (heightDp > 56 dp)
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(horizontal = 8.dp, vertical = 6.dp)
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = block.title,
                                fontWeight = FontWeight.Bold,
                                fontSize = 13.sp,
                                color = PlayfulColors.TextPrimary,
                                modifier = Modifier.weight(1f).padding(end = 4.dp),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            BlockTypeBadge(block.blockType)
                        }

                        if (hasOverlap) {
                            Spacer(modifier = Modifier.height(2.dp))
                            Surface(
                                color = PlayfulColors.WarningContainer,
                                shape = shapes.badge,
                                border = BorderStroke(1.dp, PlayfulColors.WarningBorder)
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp)
                                ) {
                                    Icon(
                                        imageVector = AppIcons.Warning,
                                        contentDescription = null,
                                        tint = PlayfulColors.OnWarningContainer,
                                        modifier = Modifier.size(10.dp)
                                    )
                                    Spacer(modifier = Modifier.width(3.dp))
                                    Text(
                                        text = "Równoległy punkt programu",
                                        fontSize = 9.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = PlayfulColors.OnWarningContainer
                                    )
                                }
                            }
                        }

                        if (block.instructions.isNotBlank() && heightDp >= 140.dp) {
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = block.instructions,
                                fontSize = 10.sp,
                                maxLines = 2,
                                overflow = TextOverflow.Ellipsis,
                                color = PlayfulColors.TextSecondary
                            )
                        }

                        Spacer(modifier = Modifier.weight(1f))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Row(horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                                IconButton(
                                    onClick = onEdit,
                                    modifier = Modifier.size(24.dp)
                                ) {
                                    Icon(
                                        imageVector = AppIcons.Edit,
                                        contentDescription = "Edytuj blok",
                                        tint = PlayfulColors.Coral,
                                        modifier = Modifier.size(13.dp)
                                    )
                                }

                                IconButton(
                                    onClick = onDelete,
                                    modifier = Modifier.size(24.dp)
                                ) {
                                    Icon(
                                        imageVector = AppIcons.Delete,
                                        contentDescription = "Usuń blok",
                                        tint = PlayfulColors.Error,
                                        modifier = Modifier.size(13.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

        // --- 2. TOP & BOTTOM DRAG HANDLES (ONLY SHOWN IN EDIT MODE FOR SELECTED BLOCK) ---
        if (isTimelineEditMode && isSelected) {
            // TOP RESIZE HANDLE (Changes start time)
            Box(
                modifier = Modifier
                    .offset(x = activeGripsGeom.topGrip.leftDp.dp, y = activeGripsGeom.topGrip.topDp.dp)
                    .size(width = activeGripsGeom.topGrip.widthDp.dp, height = activeGripsGeom.topGrip.heightDp.dp)
                    .zIndex(25f)
                    .semantics {
                        contentDescription = "Zmień początek bloku ${block.title}, obecny: ${block.startTime}"
                        role = Role.Button
                        onClick(label = topActions.first().label) {
                            topActions.first().execute()
                            true
                        }
                        customActions = topActions.map { action ->
                            CustomAccessibilityAction(action.label) {
                                action.execute()
                                true
                            }
                        }
                    }
                    .pointerInput(block.id, step5MinPx) {
                        detectVerticalDragGestures(
                            onVerticalDrag = { change, dragAmount ->
                                change.consume()
                                isDraggingTop = true
                                dragStartY += dragAmount
                                val steps = (dragStartY / step5MinPx).toInt()
                                if (steps != 0) {
                                    dragStartY -= steps * step5MinPx
                                    onResizeStart(steps * 5)
                                }
                            },
                            onDragEnd = {
                                dragStartY = 0f
                                isDraggingTop = false
                            },
                            onDragCancel = {
                                dragStartY = 0f
                                isDraggingTop = false
                            }
                        )
                    },
                contentAlignment = if (activeGripsGeom.isStaggeredHorizontally) Alignment.TopStart else Alignment.TopCenter
            ) {
                Column(
                    horizontalAlignment = if (activeGripsGeom.isStaggeredHorizontally) Alignment.Start else Alignment.CenterHorizontally,
                    modifier = Modifier.padding(top = if (activeGripsGeom.isStaggeredHorizontally) cardOffset else 0.dp)
                ) {
                    if (isDraggingTop) {
                        Surface(
                            color = PlayfulColors.TextPrimary,
                            shape = shapes.badge,
                            modifier = Modifier.padding(bottom = 2.dp)
                        ) {
                            Text(
                                text = block.startTime,
                                color = PlayfulColors.Surface,
                                fontSize = 9.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp)
                            )
                        }
                    }
                    Box(
                        modifier = Modifier
                            .width(minOf(28.dp, activeGripsGeom.topGrip.widthDp.dp))
                            .height(if (isTinyMarker) 3.dp else 4.dp)
                            .background(PlayfulColors.Coral, RoundedCornerShape(2.dp))
                    )
                }
            }

            // BOTTOM RESIZE HANDLE (Changes end time)
            Box(
                modifier = Modifier
                    .offset(x = activeGripsGeom.bottomGrip.leftDp.dp, y = activeGripsGeom.bottomGrip.topDp.dp)
                    .size(width = activeGripsGeom.bottomGrip.widthDp.dp, height = activeGripsGeom.bottomGrip.heightDp.dp)
                    .zIndex(25f)
                    .semantics {
                        contentDescription = "Zmień koniec bloku ${block.title}, obecny: ${block.endTime}"
                        role = Role.Button
                        onClick(label = bottomActions.first().label) {
                            bottomActions.first().execute()
                            true
                        }
                        customActions = bottomActions.map { action ->
                            CustomAccessibilityAction(action.label) {
                                action.execute()
                                true
                            }
                        }
                    }
                    .pointerInput(block.id, step5MinPx) {
                        detectVerticalDragGestures(
                            onVerticalDrag = { change, dragAmount ->
                                change.consume()
                                isDraggingBottom = true
                                dragEndY += dragAmount
                                val steps = (dragEndY / step5MinPx).toInt()
                                if (steps != 0) {
                                    dragEndY -= steps * step5MinPx
                                    onResizeEnd(steps * 5)
                                }
                            },
                            onDragEnd = {
                                dragEndY = 0f
                                isDraggingBottom = false
                            },
                            onDragCancel = {
                                dragEndY = 0f
                                isDraggingBottom = false
                            }
                        )
                    },
                contentAlignment = if (activeGripsGeom.isStaggeredHorizontally) Alignment.BottomEnd else Alignment.BottomCenter
            ) {
                Column(
                    horizontalAlignment = if (activeGripsGeom.isStaggeredHorizontally) Alignment.End else Alignment.CenterHorizontally,
                    modifier = Modifier.padding(bottom = if (activeGripsGeom.isStaggeredHorizontally) cardOffset else 0.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .width(minOf(28.dp, activeGripsGeom.bottomGrip.widthDp.dp))
                            .height(if (isTinyMarker) 3.dp else 4.dp)
                            .background(PlayfulColors.Coral, RoundedCornerShape(2.dp))
                    )
                    if (isDraggingBottom) {
                        Surface(
                            color = PlayfulColors.TextPrimary,
                            shape = shapes.badge,
                            modifier = Modifier.padding(top = 2.dp)
                        ) {
                            Text(
                                text = block.endTime,
                                color = PlayfulColors.Surface,
                                fontSize = 9.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun BlockTypeBadge(type: BlockType, modifier: Modifier = Modifier) {
    val shapes = LocalPlayfulShapes.current
    val (label, bg, fg, border) = when (type) {
        BlockType.CATALOG_GAME -> listOf("Katalog", PlayfulColors.TealLight, PlayfulColors.TealDark, PlayfulColors.TealBorder)
        BlockType.CUSTOM_GAME -> listOf("Własna", PlayfulColors.LilacLight, PlayfulColors.LilacDark, PlayfulColors.LilacBorder)
        BlockType.OTHER -> listOf("Inny", PlayfulColors.SunYellowLight, PlayfulColors.SunYellowDark, PlayfulColors.SunYellowBorder)
    }

    Surface(
        color = bg as Color,
        shape = shapes.pill,
        border = BorderStroke(1.dp, border as Color),
        modifier = modifier
    ) {
        Text(
            text = label as String,
            fontSize = 9.sp,
            fontWeight = FontWeight.Bold,
            color = fg as Color,
            letterSpacing = 0.2.sp,
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
        )
    }
}
